import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import SEO from "../seo";
import API from "../api/api";
import Reactions from "../components/Reactions";
import Sidebar from "../components/Sidebar";
import LatestArticlesSidebar from "../components/LatestArticlesSidebar";
import RelatedArticlesSidebar from "../components/RelatedArticlesSidebar";
import TagSidebar from "../components/TagSidebar";
import RelatedArticlesBelow from "../components/RelatedArticlesBelow";
import SocialShare from "../components/SocialShare";


export default function ArticlePage() {
  const { slug } = useParams();

  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);

  // 1️⃣ Fetch article
  useEffect(() => {
    if (!slug) return;

    setArticle(null);
    setRelated([]); // reset on slug change

    API.get(`/articles/${slug}`)
      .then(res => setArticle(res.data))
      .catch(() => setArticle(null));
  }, [slug]);

  // 2️⃣ Fetch related AFTER article loads
  useEffect(() => {
    if (!article?.slug) return;

    API.get(`/articles/related/${article.slug}`)
      .then(res => setRelated(Array.isArray(res.data) ? res.data : []))
      .catch(() => setRelated([]));
  }, [article]);


  if (!article) {
    return <div className="p-10 text-center">Loading...</div>;
  }

  // ================= HELPERS =================
  const truncate = (text, max = 55) =>
    text && text.length > max ? text.slice(0, max) + "..." : text;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // ================= TAG CLEANING =================
  const tags = Array.isArray(article.tags)
    ? article.tags.map(t => t.replace(/^#/, "").trim()).filter(Boolean)
    : typeof article.tags === "string"
      ? article.tags.split(",").map(t => t.replace(/^#/, "").trim()).filter(Boolean)
      : [];

  // ================= PAGINATION =================
  const prevArticle = related.length > 0 ? related[0] : null;
  const nextArticle = related.length > 1 ? related[1] : null;

  const author = article.author || {};

  const cleanContent = article.content
    // remove empty paragraphs
    .replace(/<p>(&nbsp;|\s)*<\/p>/gi, "")
    // remove <p><br></p>
    .replace(/<p><br\s*\/?><\/p>/gi, "")
    // remove multiple <br>
    .replace(/(<br\s*\/?>\s*){2,}/gi, "<br>");

  return (
    <>
      {/* ================= SEO ================= */}
      <SEO
        title={article.seo_title || article.title}
        description={article.meta_description || article.excerpt}
        canonical={`https://techarticlehub.com/article/${article.slug}`}
        image={article.images}
        article={article}
        author={author}
      />


      <div className="w-full max-w-7xl mx-auto mt-10 grid grid-cols-1 lg:grid-cols-3 gap-10 px-4">

        {/* ================= LEFT CONTENT ================= */}
        <div className="lg:col-span-2">

          {/* CATEGORY */}
          <span className="bg-black text-white text-xs px-3 py-1 rounded">
            {article.category || "Other"}
          </span>

          {/* TITLE */}
          <h1 className="
                  text-2xl sm:text-3xl lg:text-4xl
                  font-extrabold
                  leading-tight
                  tracking-tight
                  text-gray-900
                  mt-4
                "
          >
            {article.title}
          </h1>
          {/* META DESCRIPTION */}
          {(article.meta_description || article.excerpt) && (
            <p
              className="
      mt-3
      max-w-3xl
      text-base
      text-gray-600
      leading-relaxed
    "
            >
              {article.meta_description || article.excerpt}
            </p>
          )}
          {/* ================= SHARE (TOP) ================= */}
          <SocialShare title={article.title} />

          {/* ================= AUTHOR BOX ================= */}
          <div
            className="
    mt-8
    flex flex-col sm:flex-row
    sm:items-center sm:justify-between
    gap-4
    p-5
    rounded-xl
    bg-white
    border border-red-200
    shadow-sm
  "
          >
            {/* LEFT: AUTHOR INFO */}
            <div className="flex gap-4 items-center">
              {/* AUTHOR AVATAR */}
              <img
                src={author.image || "https://via.placeholder.com/120"}
                alt={author.name || "Author"}
                className="
        w-14 h-14
        rounded-full
        object-cover
        border-2 border-red-300
        shadow-sm
      "
              />

              {/* AUTHOR DETAILS */}
              <div>
                <p className="text-base font-semibold text-gray-900">
                  {author.name || "Unknown Author"}
                </p>

                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                  <i className="fa-regular fa-calendar"></i>
                  {formatDate(article.created_at)}
                  {article.updated_at && (
                    <>
                      <span className="mx-1">•</span>
                      Updated {formatDate(article.updated_at)}
                    </>
                  )}
                </p>

                {author.email && (
                  <p className="text-xs text-blue-600 mt-0.5">
                    {author.email}
                  </p>
                )}

                {/* SOCIAL ICONS */}
                <div className="flex gap-3 mt-2 text-sm">
                  {author.facebook && (
                    <a href={author.facebook} target="_blank" rel="noreferrer" aria-label="facebook">
                      <i className="fa-brands fa-facebook text-[#1877F2] hover:opacity-80"></i>
                    </a>
                  )}
                  {author.twitter && (
                    <a href={author.twitter} target="_blank" rel="noreferrer" aria-label="twitter">
                      <i className="fa-brands fa-twitter text-[#1DA1F2] hover:opacity-80"></i>
                    </a>
                  )}
                  {author.instagram && (
                    <a href={author.instagram} target="_blank" rel="noreferrer" aria-label="instagram">
                      <i className="fa-brands fa-instagram text-[#E1306C] hover:opacity-80"></i>
                    </a>
                  )}
                  {author.linkedin && (
                    <a href={author.linkedin} target="_blank" rel="noreferrer" aria-label="linkedin">
                      <i className="fa-brands fa-linkedin text-[#0077B5] hover:opacity-80"></i>
                    </a>
                  )}
                  {author.github && (
                    <a href={author.github} target="_blank" rel="noreferrer" aria-label="github">
                      <i className="fa-brands fa-github text-gray-800 hover:opacity-80"></i>
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT: VIEWS */}
            <div
              className="
      flex items-center gap-2
      px-4 py-2
      rounded-full
      bg-red-50
      border border-red-200
      text-red-600
      text-sm
      font-medium
      shrink-0
    "
            >
              <i className="fa-regular fa-eye"></i>
              <span>{article.views || 0}</span>
              <span className="text-xs text-gray-700">views</span>
            </div>
          </div>


          {/* ================= FEATURED IMAGE ================= */}
          {/* {article.images && (
            <img
              src={article.images}
              alt={article.title}
              width="1280"
              height="654"
              loading="eager"
              fetchpriority="high"
              decoding="async"
              className="w-full max-h-[420px] object-cover mt-8 rounded-lg border border-red-300"
            />
          )} */}
          {article.images && (
            <figure className="relative mt-8 rounded-2xl overflow-hidden">
              <img
                src={article.images}
                alt={article.title}
                loading="lazy"
                className="w-full h-[260px] sm:h-[360px] lg:h-[420px] object-cover"
              />

              {article.image_credit && (
                <figcaption className="absolute bottom-2 right-3 text-[11px] text-gray-200 bg-black/50 px-2 py-[2px] rounded">
                  Image credit: {article.image_credit}
                </figcaption>
              )}
            </figure>
          )}

          {/* ================= CONTENT ================= */}
          <div
            className="prose prose-lg mt-8 max-w-full"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
          {/* ================= SHARE (TOP) ================= */}
          <SocialShare title={article.title} />

          {/* ================= REACTIONS ================= */}
          <div className="border-t border-red-300 pt-4 mt-6">
            <Reactions initial={article.reactions || {}} />
          </div>
          {/* SECTION TITLE */}
          <h3 className="text-2xl font-bold mb-6">
            Related Topics
          </h3>
          {/* TAG LIST */}
          <div className="p-4 flex flex-wrap gap-2">
            {tags.map((tag) => {
              const cleanTag = tag.replace(/^#/, "").trim();
              const slug = cleanTag.toLowerCase().replace(/\s+/g, "-");
              // UI-friendly display text (Capitalized)
              const displayTag = cleanTag.replace(/\b\w/g, (c) => c.toUpperCase());

              return (
                <Link
                  key={slug}
                  to={`/tag/${slug}`}
                  className="
                  text-sm
                  bg-blue-100 text-blue-700
                  hover:bg-blue-200 hover:text-blue-900
                  px-3 py-1
                  rounded-md     /* ✅ SQUARE */
                  border border-blue-200
                  transition
                "
                >
                  {displayTag}
                </Link>
              );
            })}
          </div>


          {/* ✅ RELATED ARTICLES BELOW CONTENT */}
          <RelatedArticlesBelow articles={related} />


          {/* ================= PREV / NEXT ================= */}
          <div className="mt-10 border-t border-red-300 pt-6 flex flex-col sm:flex-row gap-8">

            <div className="flex-1">
              {prevArticle ? (
                <Link to={`/article/${prevArticle.slug}`}>
                  <p className="text-xs text-gray-500">← Previous</p>
                  <p className="font-semibold hover:text-blue-600">
                    {truncate(prevArticle.title)}
                  </p>
                </Link>
              ) : (
                <p className="text-sm text-gray-600">No previous article</p>
              )}
            </div>

            <div className="flex-1 text-left sm:text-right">
              {nextArticle ? (
                <Link to={`/article/${nextArticle.slug}`}>
                  <p className="text-xs text-gray-500">Next →</p>
                  <p className="font-semibold hover:text-blue-600">
                    {truncate(nextArticle.title)}
                  </p>
                </Link>
              ) : (
                <p className="text-sm text-gray-600">No next article</p>
              )}
            </div>
          </div>

          {/* ================= AUTHOR BIO ================= */}
          <div className="mt-10 border-t border-red-300 pt-6">
            {/* ================= AUTHOR BIO ================= */}
            <Link
              to={`/author/${author.slug || "naveen-kumar"}`}
              state={{ author }}   // 👈 pass full author object
              className="block mt-10 border-t border-red-300 pt-6"
            >
              <div className="bg-gradient-to-r from-red-50 to-white p-5 rounded-xl border border-red-300 flex gap-5 items-center hover:shadow-lg transition cursor-pointer">
                <img
                  src={author.image}
                  alt={author.name}
                  className="w-20 h-20 rounded-xl object-cover border-2 border-red-400"
                />

                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {author.name}
                  </h3>

                  <p className="text-sm text-gray-700 mt-1">
                    {author.bio}
                  </p>

                  <span className="inline-block mt-2 text-sm font-semibold text-red-600">
                    View full profile →
                  </span>
                </div>
              </div>
            </Link>
          </div>
          <br></br>
        </div>


        {/* ================= RIGHT SIDEBAR ================= */}
        <div className="space-y-8 hidden lg:block">
          <div className="sticky top-24 space-y-8">
            <div className="border border-red-300 rounded-lg">
              <Sidebar />
            </div>

            <div className="border border-red-300 rounded-lg">
              <LatestArticlesSidebar />
            </div>

            <div className="border border-red-300 rounded-lg">
              <RelatedArticlesSidebar articles={related} />
            </div>

            <div className="border border-red-300 rounded-lg">
              {tags.length > 0 && <TagSidebar tags={tags} />}
            </div>
            <br></br>
          </div>
          <br></br>
        </div>

      </div>
    </>
  );
}

